/* eslint-disable no-nested-ternary */
import { useState } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import SvgIcon from '@mui/material/SvgIcon';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';

import { useResponsive } from 'src/hooks/use-responsive';

import { useAuthContext } from 'src/auth/hooks';
import { SUBSCRIPTION_TYPES, SUBSCRIPTION_STATUS } from 'src/_mock';

import Label from 'src/components/label';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

// ----------------------------------------------------------------------

export default function TeamSwitcher() {
  const { teams, currentTeam, setCurrentTeam } = useAuthContext();

  const isBasic = (param = currentTeam) =>
    param?.subscription?.status !== SUBSCRIPTION_STATUS.EXPIRED &&
    param?.subscription?.productName === SUBSCRIPTION_TYPES.BASIC;

  const isPremium = (param = currentTeam) =>
    param?.subscription?.status !== SUBSCRIPTION_STATUS.EXPIRED &&
    param?.subscription?.productName === SUBSCRIPTION_TYPES.PREMIUM;

  const isEnterprise = (param = currentTeam) =>
    param?.subscription?.status !== SUBSCRIPTION_STATUS.EXPIRED &&
    param?.subscription?.productName === SUBSCRIPTION_TYPES.ENTERPRISE;

  const popover = usePopover();

  // Switch Team
  const handleSwitchTeam = (team, object) => {
    setCurrentTeam(team, object);
    popover.onClose();
  };

  // Set Current Team to null
  const handleSwitchTeamNull = () => {
    setCurrentTeam(null);
    popover.onClose();
  };

  const [isLoading, setIsLoading] = useState(true);

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    setIsLoading(false); // Stop showing loader even if there's an error
  };

  const lgUp = useResponsive('up', 'lg');

  const mdDown = useResponsive('down', 'md');

  return (
    <>
      <Button
        variant="outlined"
        onClick={popover.onOpen}
        startIcon={
          <Avatar
            alt={currentTeam?.name}
            src={currentTeam?.avatarUrl || currentTeam?.name}
            sx={{ width: 24, height: 24 }}
          />
        }
        sx={{
          textTransform: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          border: '0.3px solid rgba(0,0,0,0.12)',
          padding: '8px 16px',
          marginRight: { xs: 1, md: 2 },
        }}
      >
        {/* Team Name */}
        {lgUp && (
          <>
            <Box
              component="span"
              sx={{
                overflow: 'hidden', // Hide overflow
                textOverflow: 'ellipsis', // Add ellipsis for long text
                whiteSpace: 'nowrap', // Prevent wrapping
                maxWidth: '150px', // Optional: Set a max width for responsiveness
                flexGrow: 1,
              }}
            >
              {currentTeam?.name || 'None'}
            </Box>
            {/* Team Chip */}
            <Label
              color={
                isBasic(currentTeam) ? 'primary' : isPremium(currentTeam) ? 'warning' : isEnterprise(currentTeam) ? 'error' : currentTeam?.name ? 'default' : 'success'
              }
            >
              {(currentTeam?.subscription?.status !== SUBSCRIPTION_STATUS.EXPIRED ? currentTeam?.subscription?.productName : currentTeam?.name ? 'Expired' : 'Free') || 'Free'}
            </Label>
          </>
        )}
        {/* Arrow Icon */}
        <SvgIcon
          viewBox="0 0 32 32" // Ensure the SVG matches its original dimensions
          sx={{
            width: 18,
            height: 18, // Maintain a square size
          }}
        >
          <path
            fill="currentColor"
            d="m16 28l-7-7l1.41-1.41L16 25.17l5.59-5.58L23 21zm0-24l7 7l-1.41 1.41L16 6.83l-5.59 5.58L9 11z"
          />
        </SvgIcon>
      </Button>
      {/* Team Menu */}
      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow={mdDown ? false : "top-left"}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          padding: 0,
          width: 315, // Dynamically adjust width
          margin: 0,
        }}
      >
        <Stack sx={{ p: '4px' }}>
          {!currentTeam && (
            <MenuItem
              onClick={() => handleSwitchTeamNull()}
              sx={{
                fontWeight: 'fontWeightBold',
                fontStyle: 'italic',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: 'action.selected',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                None
              </Box>
            </MenuItem>
          )}
          {currentTeam && (
            <Box
              sx={{
                maxHeight: 300, // Limit the maximum height of the container
                overflowY: 'auto', // Enable vertical scrolling
                overflowX: 'hidden', // Prevent horizontal scrolling
              }}
            >
              {/* Order by team name alphabetically, somehow breaks the teams */}
              {teams.map((team) => {
                const isSelected = team?.team?.id === currentTeam?.id;
                return (
                  <MenuItem
                    key={team?.team?.id}
                    onClick={() => handleSwitchTeam(team?.team, team)}
                    value={isSelected}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      bgcolor: isSelected ? 'action.selected' : 'inherit',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {isLoading && (
                        <CircularProgress
                          size={24}
                          color="primary"
                          sx={{
                            position: 'relative',
                            display: 'flex',
                            marginRight: 1.5,
                          }}
                        />
                      )}
                      <Avatar
                        alt={team?.team?.name}
                        src={team?.team?.avatarUrl || team?.team?.name}
                        sx={{ width: 24, height: 24, marginRight: 1.5 }}
                        onLoad={handleLoad}
                        onError={handleError}
                      />
                      <span
                        style={{
                          overflow: 'hidden', // Hide overflow
                          textOverflow: 'ellipsis', // Add ellipsis for long text
                          whiteSpace: 'nowrap', // Prevent wrapping
                          maxWidth: '140px', // Optional: Set a max width for responsiveness
                        }}
                      >
                        {team?.team?.name}
                      </span>
                    </Box>
                    {/* Team Chip */}
                    <Label
                      color={
                        isBasic(team?.team)
                          ? 'primary'
                          : isPremium(team?.team)
                            ? 'warning'
                            : isEnterprise(team?.team)
                              ? 'error'
                                : team?.team?.name
                                  ? 'default'
                                  : 'success'
                              
                      }
                    >
                      {(team?.team?.subscription?.status !== SUBSCRIPTION_STATUS.EXPIRED ? team?.team?.subscription?.productName : team?.team?.name ? 'Expired' : 'Free') || 'Free'}
                    </Label>
                  </MenuItem>
                );
              })}
            </Box>
          )}
        </Stack>
      </CustomPopover>
    </>
  );
}
